import React, { useState, useEffect } from "react";
import "../../scss/familyTree/imageGallery.scss";

/**
 * ImageGallery Component
 * Modal for viewing and managing family member images
 * Supports Google Drive links with slider navigation
 */
const ImageGallery = ({
  member,
  images = [],
  onClose,
  onAddImage,
  onDeleteImage,
  onSetProfilePhoto,
  onSave, // New prop for saving changes
}) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showAddForm, setShowAddForm] = useState(false);
  const [googleDriveLink, setGoogleDriveLink] = useState("");
  const [images_local, setImages] = useState(images || []);
  const [addedImages, setAddedImages] = useState([]); // Track newly added images
  const [deletedImageIds, setDeletedImageIds] = useState([]); // Track deleted image IDs
  const [originalImages, setOriginalImages] = useState(images || []); // Track original state

  // Helper function to normalize Google Drive URLs to thumbnail format
  const normalizeGoogleDriveUrl = (url) => {
    if (!url) return url;

    // If it's not a Google Drive URL, return as is
    if (!url.includes("drive.google.com")) return url;

    // Extract file ID from any Google Drive URL format
    let fileId = null;

    if (url.includes("/d/")) {
      fileId = url.split("/d/")[1].split("/")[0];
    } else if (url.includes("id=")) {
      fileId = url.split("id=")[1].split("&")[0];
    }

    // If we found an ID, convert to thumbnail format with smaller size to reduce bandwidth
    // Using sz=w400 instead of w1000 to reduce rate limiting
    if (fileId) {
      return `https://drive.google.com/thumbnail?id=${fileId}&sz=w400`;
    }

    return url;
  };

  useEffect(() => {
    console.log("ImageGallery useEffect - images prop changed:", images);
    console.log("ImageGallery useEffect - current images_local:", images_local);
    // Ensure all images have required properties and filter out any null/undefined
    const validImages = (images || [])
      .filter(Boolean) // Remove null/undefined
      .map((img) => {
        const normalizedUrl = normalizeGoogleDriveUrl(img.url);
        if (normalizedUrl !== img.url) {
          console.log("🔄 Normalized Google Drive URL:");
          console.log("  Old:", img.url);
          console.log("  New:", normalizedUrl);
        }
        return {
          ...img,
          url: normalizedUrl,
          type: img.type || "gallery", // Ensure type is set
        };
      });
    console.log("ImageGallery useEffect - setting validImages:", validImages);
    setImages(validImages);
    setOriginalImages(validImages); // Track original state
    // Reset tracking when images change from outside
    setAddedImages([]);
    setDeletedImageIds([]);

    // Also log what types we have
    validImages.forEach((img, idx) => {
      console.log(
        `Image ${idx}: type=${img.type}, url=${img.url?.substring(0, 50)}...`
      );
    });
  }, [images]);

  const currentImage = images_local[currentImageIndex];

  const handlePrevious = () => {
    setCurrentImageIndex((prev) =>
      prev === 0 ? images_local.length - 1 : prev - 1
    );
  };

  const handleNext = () => {
    setCurrentImageIndex((prev) =>
      prev === images_local.length - 1 ? 0 : prev + 1
    );
  };

  const convertGoogleDriveLink = (input) => {
    // Clean the input
    const trimmedInput = input.trim();

    // If it's already a direct image URL (imgur, cloudinary, etc.), use it as is
    if (
      trimmedInput.startsWith("http") &&
      (trimmedInput.includes("imgur.com") ||
        trimmedInput.includes("cloudinary.com") ||
        trimmedInput.includes("i.ibb.co") ||
        trimmedInput.includes("postimg.cc") ||
        trimmedInput.includes("imgbb.com") ||
        trimmedInput.match(/\.(jpg|jpeg|png|gif|webp|svg)(\?|$)/i))
    ) {
      console.log("Using direct image URL:", trimmedInput);
      return trimmedInput;
    }

    // Extract file ID from Google Drive link
    let fileId = "";

    // If it's already just an ID (no slashes or https), use it directly
    if (
      !trimmedInput.includes("/") &&
      !trimmedInput.includes("https") &&
      !trimmedInput.includes("http")
    ) {
      fileId = trimmedInput;
      console.log("Detected Google Drive ID:", fileId);
    } else if (trimmedInput.includes("/d/")) {
      fileId = trimmedInput.split("/d/")[1].split("/")[0];
      console.log("Extracted ID from /d/ format:", fileId);
    } else if (trimmedInput.includes("?id=")) {
      fileId = trimmedInput.split("?id=")[1].split("&")[0];
      console.log("Extracted ID from ?id= format:", fileId);
    } else if (trimmedInput.includes("open?id=")) {
      fileId = trimmedInput.split("open?id=")[1].split("&")[0];
      console.log("Extracted ID from open?id= format:", fileId);
    } else if (trimmedInput.includes("file/d/")) {
      fileId = trimmedInput.split("file/d/")[1].split("/")[0];
      console.log("Extracted ID from file/d/ format:", fileId);
    } else if (trimmedInput.includes("uc?id=")) {
      fileId = trimmedInput.split("uc?id=")[1].split("&")[0];
      console.log("Extracted ID from uc?id= format:", fileId);
    } else {
      // If it's any other URL format, return as is
      console.log("Using URL as-is (unknown format):", trimmedInput);
      return trimmedInput;
    }

    // Return Google Drive thumbnail URL for better embedding compatibility
    // Using thumbnail endpoint which is more reliable for web embedding
    // Note: File MUST be publicly shared ("Anyone with the link can view")
    if (fileId) {
      const driveUrl = `https://drive.google.com/thumbnail?id=${fileId}&sz=w1000`;
      console.log("Generated Google Drive thumbnail URL:", driveUrl);
      console.warn("⚠️ Make sure the Google Drive file is shared publicly!");
      return driveUrl;
    }

    return trimmedInput;
  };

  const handleAddImage = () => {
    if (googleDriveLink.trim()) {
      const originalInput = googleDriveLink.trim();
      const convertedLink = convertGoogleDriveLink(originalInput);

      console.log("=== Adding Image ===");
      console.log("Original input:", originalInput);
      console.log("Converted URL:", convertedLink);

      // If this is the first image, set it as profile by default
      const isFirstImage = images_local.length === 0;

      const newImage = {
        id: Date.now(),
        url: convertedLink,
        originalInput: originalInput, // Store original for debugging
        type: isFirstImage ? "profile" : "gallery",
        caption: "",
        title: "Untitled",
        description: "",
        source: "google-drive",
        addedAt: new Date().toISOString(),
      };

      console.log("New image object:", newImage);

      setImages([...images_local, newImage]);
      setAddedImages([...addedImages, newImage]); // Track as added
      onAddImage?.(newImage);
      setGoogleDriveLink("");
      setShowAddForm(false);
    }
  };

  const handleDeleteImage = (index) => {
    const imageToDelete = images_local[index];
    if (!imageToDelete) return;

    const newImages = images_local.filter((_, i) => i !== index);
    setImages(newImages);

    // Check if this was an originally existing image or a newly added one
    const wasOriginal = originalImages.some(
      (img) => img.id === imageToDelete.id
    );

    if (wasOriginal) {
      // Track for deletion from server
      setDeletedImageIds([...deletedImageIds, imageToDelete.id]);
    } else {
      // Remove from added images list
      setAddedImages(addedImages.filter((img) => img.id !== imageToDelete.id));
    }

    if (currentImageIndex >= newImages.length && newImages.length > 0) {
      setCurrentImageIndex(newImages.length - 1);
    }

    onDeleteImage?.(imageToDelete);
  };

  const handleSetAsProfile = (index) => {
    const updatedImages = images_local
      .map((img, idx) => {
        if (!img) return null;
        return {
          ...img,
          type: idx === index ? "profile" : "gallery",
        };
      })
      .filter(Boolean);

    setImages(updatedImages);

    const selectedImage = updatedImages[index];
    if (onSetProfilePhoto && selectedImage) {
      onSetProfilePhoto(selectedImage, updatedImages);
    }
  };

  const toggleImageType = (index) => {
    const currentImage = images_local[index];
    if (!currentImage) {
      console.log("Toggle failed: No image at index", index);
      return;
    }

    console.log(
      "Toggling image type for index",
      index,
      "current type:",
      currentImage.type
    );

    const newType = currentImage.type === "profile" ? "gallery" : "profile";

    console.log("New type will be:", newType);

    // If setting as profile, all other images become gallery
    // If setting as gallery, type stays as is for other images
    const updatedImages = images_local
      .map((img, idx) => {
        if (!img) return null;
        if (idx === index) {
          return { ...img, type: newType };
        }
        // If we're setting a new profile photo, demote any existing profile photos
        if (newType === "profile" && img.type === "profile") {
          return { ...img, type: "gallery" };
        }
        return img;
      })
      .filter(Boolean);

    console.log("Updated images:", updatedImages);

    setImages(updatedImages);

    // Always notify parent of the change
    if (onAddImage) {
      console.log("Calling onAddImage with updated images");
      try {
        onAddImage(null, updatedImages);
        console.log("onAddImage call completed successfully");
      } catch (error) {
        console.error("Error calling onAddImage:", error);
      }
    } else {
      console.log("WARNING: onAddImage callback not provided");
    }
  };

  return (
    <div className="image-gallery-modal">
      <div className="gallery-overlay" onClick={onClose} />

      <div className="gallery-container">
        <div className="gallery-header">
          <h2>Gallery - {member?.name || "Member"}</h2>
          <button className="close-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        {images_local.length > 0 ? (
          <div className="gallery-content">
            <div className="image-viewer">
              <button
                className="nav-btn prev"
                onClick={handlePrevious}
                disabled={images_local.length <= 1}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="15 18 9 12 15 6"></polyline>
                </svg>
              </button>

              <div className="image-display">
                {currentImage ? (
                  <>
                    <img
                      src={currentImage.url}
                      alt={`${member?.name} ${currentImageIndex + 1}`}
                      loading="lazy"
                      style={{
                        maxWidth: "100%",
                        maxHeight: "100%",
                        objectFit: "contain",
                      }}
                      onError={(e) => {
                        console.error("❌ IMAGE LOAD ERROR");
                        console.error("URL:", currentImage.url);

                        // Check if it's a rate limit error
                        if (e.target.src.includes("drive.google.com")) {
                          e.target.style.border = "3px solid orange";
                          e.target.style.padding = "20px";
                          e.target.style.background = "#fff3e0";
                          e.target.alt = `⚠️ Google Drive Rate Limit (429). Please wait a few minutes or use a different image hosting service like Imgur or ImgBB. URL: ${currentImage.url}`;
                        } else {
                          e.target.style.border = "3px solid red";
                          e.target.style.padding = "20px";
                          e.target.style.background = "#ffebee";
                          e.target.alt = `❌ Failed to load. URL: ${currentImage.url}`;
                        }
                      }}
                      onLoad={() => {
                        console.log("✅ Image loaded:", currentImage.url);
                      }}
                    />
                    {currentImage.type === "profile" && (
                      <button
                        className="current-profile-indicator clickable"
                        onClick={() => toggleImageType(currentImageIndex)}
                        title="Click to change to gallery image"
                      >
                        <svg
                          viewBox="0 0 24 24"
                          fill="white"
                          stroke="white"
                          strokeWidth="2"
                        >
                          <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                          <circle cx="12" cy="7" r="4" />
                        </svg>
                        <span>Profile Photo</span>
                      </button>
                    )}
                    {currentImage.type === "gallery" && (
                      <button
                        className="current-gallery-indicator clickable"
                        onClick={() => toggleImageType(currentImageIndex)}
                        title="Click to set as profile photo"
                      >
                        <svg
                          viewBox="0 0 24 24"
                          fill="white"
                          stroke="white"
                          strokeWidth="2"
                        >
                          <rect
                            x="3"
                            y="3"
                            width="18"
                            height="18"
                            rx="2"
                            ry="2"
                          />
                          <circle cx="8.5" cy="8.5" r="1.5" />
                          <path d="M21 15l-5-5L5 21" />
                        </svg>
                        <span>Gallery</span>
                      </button>
                    )}
                    <span className="image-counter">
                      {currentImageIndex + 1} / {images_local.length}
                    </span>
                  </>
                ) : (
                  <div className="no-image">No Image</div>
                )}
              </div>

              <button
                className="nav-btn next"
                onClick={handleNext}
                disabled={images_local.length <= 1}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="9 18 15 12 9 6"></polyline>
                </svg>
              </button>
            </div>

            <div className="gallery-thumbnails">
              {images_local.filter(Boolean).map((img, index) => {
                if (!img) return null;
                const isProfilePhoto = img.type === "profile";
                return (
                  <div
                    key={index}
                    className={`thumbnail ${
                      index === currentImageIndex ? "active" : ""
                    } ${isProfilePhoto ? "profile-photo" : ""}`}
                    onClick={() => setCurrentImageIndex(index)}
                  >
                    <img src={img.url} alt={`Thumbnail ${index + 1}`} />
                    {/* Type badge - always visible and clickable */}
                    <button
                      className={`type-badge ${
                        isProfilePhoto ? "profile" : "gallery"
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleImageType(index);
                      }}
                      title={
                        isProfilePhoto
                          ? "Profile Photo (click to change to gallery)"
                          : "Gallery Image (click to set as profile)"
                      }
                    >
                      {isProfilePhoto ? (
                        <>
                          <svg
                            viewBox="0 0 24 24"
                            fill="white"
                            stroke="white"
                            strokeWidth="2"
                          >
                            <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                            <circle cx="12" cy="7" r="4" />
                          </svg>
                          <span>Profile</span>
                        </>
                      ) : (
                        <>
                          <svg
                            viewBox="0 0 24 24"
                            fill="white"
                            stroke="white"
                            strokeWidth="2"
                          >
                            <rect
                              x="3"
                              y="3"
                              width="18"
                              height="18"
                              rx="2"
                              ry="2"
                            />
                            <circle cx="8.5" cy="8.5" r="1.5" />
                            <path d="M21 15l-5-5L5 21" />
                          </svg>
                          <span>Gallery</span>
                        </>
                      )}
                    </button>
                    <div className="thumbnail-actions">
                      <button
                        className="delete-thumbnail"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteImage(index);
                        }}
                        title="Delete image"
                      >
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                          <path d="M10 11v6M14 11v6" />
                        </svg>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="gallery-actions">
              <button
                className="btn-add-image"
                onClick={() => setShowAddForm(!showAddForm)}
                title="Add Image"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 8v8M8 12h8" />
                </svg>
              </button>
              <button
                className="btn-delete-image"
                onClick={() => handleDeleteImage(currentImageIndex)}
                disabled={images_local.length === 1}
                title="Delete Current Image"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                  <path d="M10 11v6M14 11v6" />
                </svg>
              </button>
              <button
                className="btn-done-gallery"
                onClick={() => {
                  // Call onSave if provided with the changes
                  if (onSave) {
                    const profileImage = images_local.find(
                      (img) => img.type === "profile"
                    );
                    onSave({
                      images: addedImages.length > 0 ? addedImages : undefined,
                      removeImages:
                        deletedImageIds.length > 0
                          ? deletedImageIds
                          : undefined,
                      profileImageUrl: profileImage?.url || "",
                      allImages: images_local, // Pass all current images for local state update
                    });
                  }
                  onClose();
                }}
                title="Done"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M20 6L9 17l-5-5" />
                </svg>
              </button>
            </div>
          </div>
        ) : (
          <div className="gallery-empty">
            <p>📷 No images yet</p>
            <button
              className="btn-add-image"
              onClick={() => setShowAddForm(true)}
              title="Add First Image"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="12" cy="12" r="10" />
                <path d="M12 8v8M8 12h8" />
              </svg>
              <span>Add First Image</span>
            </button>
          </div>
        )}

        {showAddForm && (
          <div className="add-image-form">
            <h3>Add Image from Google Drive</h3>

            <div className="instructions">
              <p>
                <strong>📝 How to add an image:</strong>
              </p>
              <ol>
                <li>Open Google Drive and find your image</li>
                <li>Right-click the image → Share</li>
                <li>Change sharing to "Anyone with the link"</li>
                <li>Copy the link and paste below (or just the ID)</li>
              </ol>
              <p className="example">
                <strong>Example:</strong>
                <br />
                Full link:{" "}
                <code>
                  https://drive.google.com/file/d/
                  <span style={{ color: "#e53e3e", fontWeight: "bold" }}>
                    1KhF1AkmTX9anc6jesfxH-3PMy0tmy1ma
                  </span>
                  /view?usp=sharing
                </code>
                <br />
                Or just the ID:{" "}
                <code style={{ color: "#e53e3e", fontWeight: "bold" }}>
                  1KhF1AkmTX9anc6jesfxH-3PMy0tmy1ma
                </code>
              </p>
            </div>

            <div className="form-group">
              <label htmlFor="drive-link">Image URL or Google Drive ID:</label>
              <input
                id="drive-link"
                type="text"
                placeholder="Paste image URL or Google Drive ID"
                value={googleDriveLink}
                onChange={(e) => setGoogleDriveLink(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    handleAddImage();
                  }
                }}
              />
            </div>

            <div className="form-actions">
              <button
                className="btn-confirm"
                onClick={handleAddImage}
                title="Add Image"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M20 6L9 17l-5-5" />
                </svg>
                <span>Add Image</span>
              </button>
              <button
                className="btn-cancel"
                onClick={() => {
                  setShowAddForm(false);
                  setGoogleDriveLink("");
                }}
                title="Cancel"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
                <span>Cancel</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImageGallery;
